import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Dropdown,
  Dialog,
  DialogType,
  DialogFooter,
  MessageBar,
  MessageBarType,
  Spinner,
  ActionButton,
  Persona,
  PersonaCoin,
  PersonaSize
} from "@fluentui/react";
import { Link } from 'react-router-dom';
import MultiPickerFluent from "../components/MultiPicker";
import { Chip, styles } from "./ProposalDashboard";

/**
 * ProjectsManager (Compass Projects)
 * - Lists projects with search/filter/sort/pagination
 * - Create / Update in a right-side Panel
 * - Delete with confirmation dialog
 * - Uses /api/compass_projects and /api/clients
 */
export default function ProjectsManager(props) {
  // table state
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1, sort: "summary_end_date", order: "desc", search: ""});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFrom, setFilterFrom] = useState(null); // Date
  const [filterTo, setFilterTo] = useState(null);
  const [filterClients, setFilterClients] = useState([]); // client id
  const [filterPeople, setFilterPeople] = useState([]);
  const [filterPm, setFilterPm] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [params, setParams] = useState({});
  const [status, setStatus] = useState("");

  // clients lookup
  const [clients, setClients] = useState([]); // {key:id, text:name}
  const [tags, setTags] = useState([]);
  const [people, setPeople] = useState([]);
  
  const clientTextById = useMemo(() => Object.fromEntries(clients.map(c => [c.key, c.text])), [clients]);

  // delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, title}

  // toast
  const [toast, setToast] = useState(null); // {type, text}

  // columns
  const columns = useMemo(() => {
    const onColumnClick = (ev, col) => {
      if (!col) return;
      const isSame = meta.sort === col.fieldName;
      const nextOrder = isSame && meta.order === "asc" ? "desc" : "asc";
      setMeta((m) => ({ ...m, sort: col.fieldName, order: nextOrder }));
    };
    return [
      { 
        key: "project_number", 
        name: "P#", 
        fieldName: "project_number", 
        maxWidth: 40,
        minWidth: 40,
        width: 40,
        isResizable: false, 
        onColumnClick 
      },
      {
        key: 'title',
        name: 'Title',
        minWidth: 200,
        isResizable: true,
        onRender: (item) => {
          const text = item.title || item.name || item.project_number || `Project ${item.id}`;
          return (
            <Link to={`/projects/${item.id}`} styles={{ root: { overflowWrap: 'anywhere' } }}>
              {text}
            </Link>
          );
        },
      },
      {
        key: 'status',
        name: "Status",
        fieldName: "status",
        isResizable: true,
        maxWidth: 40,
        onRender: ({status}) => {
          return <Chip size={12} variant={status} onClick={() => setFilterStatus(status)}>{status}</Chip>
        }
      },
      {
        key: "pm",
        name: "PM",
        maxWidth: 24,
        minWidth: 24,
        width: 24,
        isResizable: false,
        onRender: ({compass_pm_ref="", id}) => {
          return <PersonaCoin style={{cursor: 'pointer'}} onClick={() => setFilterPm(prev => prev.includes(compass_pm_ref) ? prev.filter(el => el !== compass_pm_ref) : prev.concat(compass_pm_ref))} size={PersonaSize.size24} text={compass_pm_ref} key={id} />
        }
      },
      {
        key: "staff",
        name: "Team",
        minWidth: 120,
        isResizable: true,
        onRender: ({staff=[]}) => {
          return <Stack horizontal wrap tokens={{childrenGap: 4}}>
            {staff.map(p => <PersonaCoin style={{cursor: 'pointer'}} onClick={() => setFilterPeople(prev => prev.includes(p.id) ? prev.filter(el => el !== p.id) : prev.concat(p.id))} size={PersonaSize.size24} text={p.name} key={p.id} />)}
          </Stack>
        }
      },
      {
        key: "tags",
        name: "Tags",
        minWidth: 200,
        isResizable: true,
        onRender: ({tags=[]}) => {
          return <Stack horizontal wrap tokens={{childrenGap: 4}}>
            {tags.map(t => <Chip key={t.id} size={12} onClick={() => setFilterTags(prev => prev.includes(t.id) ? prev.filter(el => el !== t.id) : prev.concat(t.id))}>{t.name}</Chip>)}
          </Stack>
        }
      },
      { 
        key: "client_friendly_name", 
        name: "Client", 
        fieldName: "client_friendly_name", 
        minWidth: 180, 
        isResizable: true, 
        onColumnClick ,
        onRender: (item) => (
          <Link to={`/clients/${item.client_id}`}>
            {item.client_friendly_name}
          </Link>
        )
      },
    ];
  }, [meta.sort, meta.order, clientTextById]);

  // fetch clients for dropdown
  const loadClients = useCallback(async () => {
    try {
      const res = await axios.get("/api/clients", { params: { limit: 1000, page: 1, sort: "name", order: "asc" } });
      const opts = (res.data?.data || []).map((c) => ({ key: c.id, text: c.name, ...c }));
      setClients(opts);
    } catch (e) {
      // Non-fatal; keep empty list
    }
  }, []);

    // fetch clients for dropdown
  const loadPeople = useCallback(async () => {
    try {
      const res = await axios.get("/api/people", { params: { active: true, limit: 1000, page: 1, sort: "name", order: "asc" } });
      const opts = (res.data?.data || []).map((p) => ({ key: p.id, text: p.name, ...p }));
      setPeople(opts);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const res = await axios.get("/api/tags");
      const opts = (res.data || []).map(t => ({key: t.id, ...t}));
      setTags(opts);
    } catch (e) {
      // non-fatal
    }
  }, []);

  // fetch projects list
  const fetchProjects = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError("");

    try {

      const params = {
        page: overrides.page ?? meta.page,
        limit: overrides.limit ?? meta.limit,
        sort: overrides.sort ?? meta.sort,
        order: overrides.order ?? meta.order,
        search: overrides.search ?? debouncedSearch,
      };
      if (filterClients.length) params.clients = filterClients.join("&&");
      if (filterPm.length) params.pms = filterPm.join("&&");
      if (filterStatus?.trim()) params.status = filterStatus.trim();
      if (filterFrom) params.from = formatDateParam(filterFrom);
      if (filterTo) params.to = formatDateParam(filterTo);
      if (filterPeople.length) params.staff = filterPeople.join("&&");
      if (filterTags.length) params.tags = filterTags.join("&&");

      const res = await axios.get("/api/compass_projects", { params });
      setItems(res.data.data || []);
      setMeta(res.data.meta || { page: 1, limit: 10, total: 0, totalPages: 1, sort: "created_at", order: "desc", search: "" });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [meta.page, meta.limit, meta.sort, meta.order, debouncedSearch, filterClients, filterPm, filterPeople, filterTags, filterStatus, filterFrom, filterTo, params]);

  const clearFilters = () => {
    setFilterClients([]);
    setFilterPeople([]);
    setSearch("");
    setFilterTags([]);
    setFilterPm([]);
    setFilterFrom(null);
    setFilterTo(null);
    setFilterStatus("");
  }

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => { loadTags(); }, [loadTags]);
  useEffect(() => { fetchProjects({ page: 1 }); }, [debouncedSearch, filterClients, filterStatus, filterFrom, filterTo, filterPm, filterPeople, filterTags]);
  useEffect(() => { fetchProjects(); }, [meta.page, meta.limit, meta.sort, meta.order]);

  // CRUD helpers
  function openCreate() {
    props.history.push('/projects/create');
  }


  async function confirmDelete() {
    try {
      await axios.delete(`/api/compass_projects/${deleteTarget.id}`);
      setToast({ type: MessageBarType.success, text: `Deleted ${deleteTarget.title}` });
      setDeleteTarget(null);
      await fetchProjects();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Delete failed" });
    }
  }

  return (
    <Stack tokens={{ childrenGap: 12 }} className={styles.root}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="end" tokens={{ childrenGap: 12 }}>
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="xxLarge">Projects</Text>
          <Text variant="small">Manage Compass projects linked to proposals and documents.</Text>
        </Stack>
        <ActionButton iconProps={{ iconName: "Add" }} text="New project" onClick={openCreate} />
      </Stack>

      {/* Filters */}
      <Stack className={styles.sectionCard}>
        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
          <Stack.Item grow={2}>
            <TextField label="Search" value={search} onChange={(_, v) => setSearch(v || "")} placeholder="Title, Project Number, Personelle" />
          </Stack.Item>
          <Stack.Item grow={1}>
            <MultiPickerFluent
              options={clients}
              label="Clients"
              selected={filterClients}
              searchKey="text"
              onAdd={(item) => setFilterClients(prev => prev.concat(item.id))} 
              onRemove={(item) => setFilterClients(prev => prev.filter(id => id !== item.id))} />
          </Stack.Item>
          <Stack.Item grow={1}>
            <Dropdown
              label="Status"
              selectedKey={filterStatus}
              onChange={(_, opt) => setFilterStatus(opt.key)}
              options={[
                { key: 'Open', text: 'Open' },
                { key: 'Closed', text: 'Closed' },
                { key: 'On Hold', text: 'On Hold' },
                { key: "", text: "All"}
              ]}
            />
          </Stack.Item>
        </Stack>
        <Stack horizontal tokens={{childrenGap: 12}} verticalAlign="end">
          <Stack.Item grow={1}>
            <MultiPickerFluent 
              options={people.map(p => ({...p, id: p.name}))}
              label="PM"
              selected={filterPm}
              onRenderItem={(item) => <Persona key={item.id} imageUrl={item.image_url} text={item.name} />}
              searchKey="name"
              onAdd={(item) => setFilterPm(prev => prev.concat(item.id))}
              onRemove={(item) => setFilterPm(prev => prev.filter(el => el !== item.id))} />
          </Stack.Item>
          <Stack.Item grow={1}>
            <MultiPickerFluent 
              options={people}
              label="Staff"
              selected={filterPeople}
              onRenderItem={(item) => <Persona key={item.id} imageUrl={item.image_url} text={item.name} />}
              searchKey="name"
              onAdd={(item) => setFilterPeople(prev => prev.concat(item.id))}
              onRemove={(item) => setFilterPeople(prev => prev.filter(el => el !== item.id))} />
          </Stack.Item>
          <Stack.Item grow={1}>
            <MultiPickerFluent 
              options={tags}
              label="Tags"
              selected={filterTags}
              searchKey="name"
              onAdd={(item) => setFilterTags(prev => prev.concat(item.id))}
              onRemove={(item) => setFilterTags(prev => prev.filter(el => el !== item.id))} />
          </Stack.Item>

          {/* <TextField label="Status" value={filterStatus} onChange={(_, v) => setFilterStatus(v || "")} styles={{ root: { width: 180 } }} /> */}
          <Stack.Item align="end">
            <DefaultButton primary text="Apply" styles={{root: {margin: 0}}} onClick={() => fetchProjects({ page: 1, search })} />
            <ActionButton primary text="Clear Filters" styles={{root: {margin: 0}}} onClick={() => clearFilters()} />
          </Stack.Item>

        </Stack>
      </Stack>

      {/* <ProjectFiltersBar onApply={setParams} onReset={() => setParams({})} /> */}

      {toast && (
        <MessageBar messageBarType={toast.type} isMultiline={false} onDismiss={() => setToast(null)}>
          {toast.text}
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError("")}>{error}</MessageBar>
      )}

      <div style={styles.sectionCard}>
        {loading ? (
          <Stack horizontal horizontalAlign="center" styles={{ root: { padding: 40 } }}>
            <Spinner label="Loading projects…" />
          </Stack>
        ) : (
          <DetailsList
            items={items}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />
        )}
      </div>

      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="small">Page {meta.page} of {meta.totalPages} · {meta.total} total</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <ActionButton text="Prev" disabled={meta.page <= 1} onClick={() => setMeta((m) => ({ ...m, page: Math.max(1, m.page - 1) }))} />
          <ActionButton text="Next" disabled={meta.page >= meta.totalPages} onClick={() => setMeta((m) => ({ ...m, page: Math.min(m.totalPages, m.page + 1) }))} />
        </Stack>
      </Stack>

      {/* Delete confirm */}
      <Dialog
        hidden={!deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
        dialogContentProps={{ type: DialogType.largeHeader, title: "Delete project", subText: deleteTarget ? `Delete ${deleteTarget.title}? This cannot be undone.` : "" }}
      >
        <DialogFooter>
          <PrimaryButton onClick={confirmDelete} text="Delete" />
          <DefaultButton onClick={() => setDeleteTarget(null)} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
}

// ===== helpers =====
function emptyProject() {
  return {
    id: undefined,
    project_number: null,
    title: "",
    client_id: null,
    status: null,
    billable_amount: null,
    summary_start_date: null,
    summary_end_date: null,
    currency: null,
    sharepoint_id: null,
    folder_link: null,
    compass_pm_ref: null,
    team_members_ref: null,
    client_friendly_name: null,
  };
}

function asDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function formatDateParam(d) {
  if (!d) return undefined;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateDisplay(val) {
  const d = asDate(val);
  if (!d) return "";
  return d.toLocaleDateString();
}

function normalizeProjectPayload(f) {
  const payload = { ...f };
  // Convert Date objects to YYYY-MM-DD (backend accepts ISO or date-only)
  if (payload.summary_start_date instanceof Date) payload.summary_start_date = formatDateParam(payload.summary_start_date);
  if (payload.summary_end_date instanceof Date) payload.summary_end_date = formatDateParam(payload.summary_end_date);
  // Empty strings -> null
  Object.keys(payload).forEach((k) => {
    if (payload[k] === "") payload[k] = null;
  });
  return payload;
}

function useDebouncedValue(value, delayMs) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function RequiredTextField({ label, value, onChange }) {
  const [touched, setTouched] = useState(false);
  const errorMessage = touched && !String(value || "").trim() ? `${label} is required` : undefined;
  return (
    <TextField
      required
      label={label}
      value={value}
      onChange={(_, v) => onChange(v || "")}
      onBlur={() => setTouched(true)}
      errorMessage={errorMessage}
    />
  );
}
